import { IconAtom2Outline18 as Atom } from "nucleo-ui-outline-18"

import { cn, parseThreadActivity } from "@productlane/lib/src/browser"
import { formatRelativeTime } from "@productlane/lib/src/date"
import { Tooltip } from "@productlane/ui"
import { SYSTEM_EVENT_RAIL_CLASS } from "@productlane/ui/src/components/slack-style-message-surface"

import type { TimelineEventProps } from "./types"

const KIND_LABELS = {
	translation: "Translation",
	summary: "Summary",
	note: "Note",
} as const

export function RoutineNoteEvent({
	event,
	hasEventAbove = false,
	hasEventBelow = false,
}: TimelineEventProps) {
	if (event.type !== "activity") return null
	if (event.threadActivity.eventType !== "routine_note") return null

	let parsed
	try {
		parsed = parseThreadActivity(
			event.threadActivity.eventType,
			event.threadActivity.payload,
		)
	} catch {
		return null
	}
	if (parsed.eventType !== "routine_note") return null

	const { kind, body, title, sourceLanguage } = parsed.payload
	const label =
		kind === "translation" && sourceLanguage
			? `Translated from ${sourceLanguage}`
			: KIND_LABELS[kind]
	const createdAt = new Date(event.threadActivity.createdAt)

	return (
		<div
			className={cn(
				"flex max-w-full flex-nowrap gap-2.5 text-xs",
				SYSTEM_EVENT_RAIL_CLASS,
				hasEventAbove ? "pt-0" : "pt-1",
				hasEventBelow ? "pb-0" : "pb-1",
			)}
		>
			<div className="flex w-8 min-w-8 flex-col items-center">
				<div
					className={cn(
						"h-[5px] w-[0.5px]",
						hasEventAbove
							? "bg-gray-300 dark:bg-gray-500"
							: "bg-gradient-to-t from-gray-300 to-transparent dark:from-gray-600",
					)}
				/>
				<div className="flex items-center justify-center rounded-full p-[2px]">
					<Atom className="h-3.5 w-3.5 flex-none text-secondary" />
				</div>
				<div
					className={cn(
						"w-[0.5px] flex-1",
						hasEventBelow
							? "bg-gray-300 dark:bg-gray-500"
							: "bg-gradient-to-b from-gray-300 to-transparent dark:from-gray-600",
					)}
				/>
			</div>

			<div className="min-w-0 flex-1 rounded-[10px] border border-gray-200 bg-gray-50 px-3 py-2.5 dark:border-gray-700/80 dark:bg-gray-900/40">
				<div className="mb-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
					<span className="text-[11.5px] font-medium text-secondary">
						{title ?? label}
					</span>
					<span className="text-tertiary">&middot;</span>
					{title ? (
						<>
							<span className="text-[11px] text-tertiary">{label}</span>
							<span className="text-tertiary">&middot;</span>
						</>
					) : null}
					<Tooltip label="Only your team sees this. It never reaches the customer.">
						<span className="text-[11px] text-tertiary">Internal</span>
					</Tooltip>
					<span className="text-tertiary">&middot;</span>
					<Tooltip label={createdAt.toLocaleString()}>
						<span className="whitespace-nowrap text-[11px] text-tertiary">
							{formatRelativeTime(createdAt, true)}
						</span>
					</Tooltip>
				</div>
				<p className="whitespace-pre-wrap text-[13px] leading-relaxed text-primary">
					{body}
				</p>
			</div>
		</div>
	)
}
